// src/components/alerts/AlertCommandHeader.js
// --- UPDATE (Alert System AI) — NEW: page header — title, live WebSocket state, FlowSense AI
// health, last update, sound, export and refresh.
import React from 'react';
import PropTypes from 'prop-types';
import {
    Box, Flex, HStack, Heading, Text, Icon, IconButton, Tooltip, Menu, MenuButton, MenuList, MenuItem,
    Badge, useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { MdWifi, MdWifiOff, MdVolumeUp, MdVolumeOff, MdDownload, MdRefresh, MdAutoAwesome, MdBugReport } from 'react-icons/md';
import { FiAlertTriangle } from 'react-icons/fi';
import { healthColor } from './alertUtils';

const MotionBox = motion(Box);

function AlertCommandHeader({
    isConnected, health, lastUpdated, soundEnabled, onToggleSound, onExport, onRefresh, isRefreshing, onOpenPerf,
}) {
    const muted = useColorModeValue('gray.500', 'gray.400');
    const gradient = useColorModeValue('linear(to-r, blue.500, teal.500, purple.500)', 'linear(to-r, blue.300, teal.300, purple.300)');
    const liveBg = useColorModeValue(isConnected ? 'green.50' : 'red.50', isConnected ? 'green.900' : 'red.900');
    const liveColor = useColorModeValue(isConnected ? 'green.700' : 'red.700', isConnected ? 'green.200' : 'red.200');
    const aiBg = useColorModeValue('purple.50', 'purple.900');

    return (
        <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} mb={5} flexWrap="wrap" gap={4}>
            <HStack spacing={3} align="center">
                <Box w="48px" h="48px" bgGradient={gradient} borderRadius="xl" display="flex" alignItems="center" justifyContent="center" boxShadow="lg">
                    <Icon as={FiAlertTriangle} color="white" boxSize={6} />
                </Box>
                <Box>
                    <Heading as="h1" size="lg" bgGradient={gradient} bgClip="text" fontWeight="800" lineHeight="1.2">
                        Alert System
                    </Heading>
                    <Text fontSize="sm" color={muted}>
                        Sensor alerts, rules and FlowSense AI monitoring
                        {lastUpdated ? ` · updated ${lastUpdated.toLocaleTimeString()}` : ''}
                    </Text>
                </Box>
            </HStack>

            <HStack spacing={2} flexWrap="wrap">
                <Tooltip hasArrow label={isConnected ? 'Live: alerts arrive the moment they are raised' : 'WebSocket offline — refresh to see new alerts'}>
                    <Flex align="center" bg={liveBg} color={liveColor} px={3} py={1.5} borderRadius="full">
                        <Icon as={isConnected ? MdWifi : MdWifiOff} mr={2} />
                        <Text fontSize="xs" fontWeight="800" textTransform="uppercase">{isConnected ? 'Live' : 'Offline'}</Text>
                        {isConnected && (
                            <MotionBox ml={2} w="8px" h="8px" bg="green.400" borderRadius="full"
                                animate={{ opacity: [1, 0.3, 1], scale: [1, 1.3, 1] }} transition={{ duration: 2, repeat: Infinity }} />
                        )}
                    </Flex>
                </Tooltip>

                {health && (
                    <Tooltip hasArrow label={health.deductions?.length ? health.deductions.map(d => `−${d.pts}: ${d.why}`).join(' · ') : 'No deductions'}>
                        <Flex align="center" bg={aiBg} px={3} py={1.5} borderRadius="full" gap={2}>
                            <Icon as={MdAutoAwesome} color="purple.400" />
                            <Text fontSize="xs" fontWeight="800">FlowSense AI</Text>
                            <Badge colorScheme={healthColor(health.score)} borderRadius="full">{health.score} · {health.label}</Badge>
                        </Flex>
                    </Tooltip>
                )}

                <Tooltip hasArrow label={soundEnabled ? 'Sound on for new critical / high alerts' : 'Sound off'}>
                    <IconButton aria-label="Toggle alert sound" icon={<Icon as={soundEnabled ? MdVolumeUp : MdVolumeOff} />}
                        variant="ghost" colorScheme={soundEnabled ? 'green' : 'gray'} borderRadius="xl" onClick={() => onToggleSound(!soundEnabled)} />
                </Tooltip>
                <Menu>
                    <MenuButton as={IconButton} aria-label="Export this page" title="Export this page" icon={<Icon as={MdDownload} />} variant="ghost" borderRadius="xl" />
                    <MenuList>
                        <MenuItem icon={<MdDownload />} onClick={() => onExport('csv')}>Export as CSV</MenuItem>
                        <MenuItem icon={<MdDownload />} onClick={() => onExport('json')}>Export as JSON</MenuItem>
                    </MenuList>
                </Menu>
                <Tooltip hasArrow label="Refresh everything (and re-run FlowSense AI)">
                    <IconButton aria-label="Refresh" icon={<Icon as={MdRefresh} />} variant="ghost" borderRadius="xl" onClick={onRefresh} isLoading={isRefreshing} />
                </Tooltip>
                {onOpenPerf && (
                    <Tooltip hasArrow label="Performance (development)">
                        <IconButton aria-label="Performance" icon={<Icon as={MdBugReport} />} variant="ghost" colorScheme="purple" borderRadius="xl" onClick={onOpenPerf} />
                    </Tooltip>
                )}
            </HStack>
        </Flex>
    );
}

AlertCommandHeader.propTypes = {
    isConnected: PropTypes.bool,
    health: PropTypes.object,
    lastUpdated: PropTypes.instanceOf(Date),
    soundEnabled: PropTypes.bool,
    onToggleSound: PropTypes.func.isRequired,
    onExport: PropTypes.func.isRequired,
    onRefresh: PropTypes.func.isRequired,
    isRefreshing: PropTypes.bool,
    onOpenPerf: PropTypes.func,
};

export default React.memo(AlertCommandHeader);
// --- END UPDATE
